// Reply Radar — Web Push subscription helpers.
import api from "@/lib/api";

const SW_URL = "/sw.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function getRegistration() {
  if (!pushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
}

export async function getExistingSubscription() {
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/** Ensure the service worker is registered + a browser subscription exists.
 *  Returns the PushSubscription (JSON) or throws with a user-facing message.
 */
export async function subscribeToPush() {
  if (!pushSupported()) throw new Error("This browser doesn't support web push");

  const { data: cfg } = await api.get("/notifications/push/config");
  if (!cfg?.enabled || !cfg.vapid_public_key) throw new Error("Push not configured on the server");

  // Permission
  const perm = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission denied");

  const reg = await getRegistration();
  if (!reg) throw new Error("Service worker registration failed");

  // Reuse existing subscription if present
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(cfg.vapid_public_key),
    });
  }
  const json = sub.toJSON();
  await api.post("/notifications/push/subscribe", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    user_agent: navigator.userAgent || "",
  });
  return json;
}

export async function unsubscribeFromPush() {
  const sub = await getExistingSubscription();
  if (!sub) return { ok: true, alreadyGone: true };
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (_e) { /* ignore browser errors */ }
  try {
    await api.post("/notifications/push/unsubscribe", { endpoint });
  } catch (_e) { /* server may already be clean */ }
  return { ok: true };
}

export async function testPush() {
  const { data } = await api.post("/notifications/push/test");
  return data;
}
